import './hello-bar.scss'
import { RichText } from '@cms/components/richText'
import { useStoreContext } from '@common/contexts/useStoreContext'
import { getSingleGlobalElementId } from '@common/utils/getSingleGlobalElementId'
import { setSessionStorage } from '@common/utils/session/setSessionStorage'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { useI18n } from '@vista-digital/occ-ui'
import { occUiVersion } from '@vista-digital/occ-ui/src/version'
import { observer } from 'mobx-react-lite'
import { type FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { cmsDataService } from '@/content/services/cms.data.service'
import { cmsService } from '@/content/services/cms.service'
import { extensions } from '@/content/services/extensions'
import type { HelloBarPropsApi } from './models'
import { getHelloBarStorageData, getUpdatedStorageData } from './utils'

export const HelloBar: FC = observer(() => {
    const logger = useLogger('HelloBar')
    const i18n = useI18n()

    const [enabledHelloBar, setEnabledHelloBar] = useState<HelloBarPropsApi | undefined>()
    const [showHelloBar, setShowHelloBar] = useState<boolean>(false)
    const { announcementsStore } = useStoreContext()

    const location = useLocation()
    const initialHelloBarsData = cmsDataService.get()?.hellobars

    // Value logged for debugging purposes
    logger.trace('file: HelloBar.tsx:20 ~ initialHelloBarsData:', initialHelloBarsData)

    useEffect(() => {
        void (async () => {
            const singleHelloBarId = getSingleGlobalElementId(location.pathname, initialHelloBarsData)

            try {
                if (!singleHelloBarId) {
                    setShowHelloBar(false)
                    return
                }
                const { helloBarStorageData } = getHelloBarStorageData(singleHelloBarId)

                const singleHelloBar = (
                    await cmsService.getExtensions(extensions.HelloBar, singleHelloBarId.toString())
                )[0]

                const updatedHelloBarStorageData = JSON.stringify(
                    getUpdatedStorageData(helloBarStorageData, singleHelloBar, singleHelloBarId)
                )

                setEnabledHelloBar(singleHelloBar)

                setShowHelloBar(true)
                setSessionStorage('hello-bar-data', updatedHelloBarStorageData)
            } catch (error: unknown) {
                logger.error(`Error retrieving HelloBar id ${singleHelloBarId}.`, { error })
            }
        })()
    }, [initialHelloBarsData, location, logger])

    const isShowing = !!enabledHelloBar && showHelloBar && announcementsStore.canShowHelloBar
    if (!isShowing) return null

    const displayMessage = i18n.template(enabledHelloBar.helloBarText, {
        appVersion: APP_VERSION,
        buildTime: new Date(APP_BUILD_TIME),
        occVersion: occUiVersion,
        connectVersion: cmsDataService.get()?.connectVersion,
    })

    return (
        <div className="hello-bar">
            <div className="container-lg-padding">
                <div className="hello-bar__content">
                    <RichText htmlText={displayMessage} />
                </div>
            </div>
        </div>
    )
})
